import { useQuery } from '@apollo/client'
import { IconCheckbox, IconIdBadge2, IconSignature } from '@tabler/icons-react'
import { t } from 'i18next'
import { sortBy } from 'lodash'

import { CommandGroup, CommandType, DynamicActionsProps } from './config'

import { TimezonedDate } from '@/context/timezoneContext'
import { IconOnlyInductionTag } from '@/modules/inductions/InductionTag'
import { BabyTicket } from '@/modules/tickets/BabyTicket'
import { GET_WORKER } from '@/modules/workers/graphQL'
import { getWorkerName } from '@/utils/getName'
import { routes } from '@/utils/index'

import { WorkerUiQuery, WorkerUiQueryVariables } from 'types/api.graphql'

type Worker = Parameters<typeof getWorkerName>[0] & {
  id: string
}

export const getWorkerInfoDefaultActions = (worker: Worker): CommandType[] => [
  {
    id: `goToWorker-${worker.id}`,
    name: getWorkerName(worker),
    href: routes.workers.view(worker.id),
  },
]

type Props = DynamicActionsProps & {
  workerIds: string[]
}

export const DynamicWorkerInfoActions: React.FC<Props> = ({
  activePage, // will be the selected workerId
  workerIds,
  replaceParentSubItems,
}) => {
  useQuery<WorkerUiQuery, WorkerUiQueryVariables>(GET_WORKER, {
    variables: {
      workerId: activePage,
    },
    skip: !workerIds?.includes(activePage),
    onCompleted: (data) => {
      const hasTickets = !!data?.worker?.tickets?.length
      const hasInductions = !!data?.worker?.inductions?.length
      const hasSwms = !!data?.worker?.documentSignatures?.length

      replaceParentSubItems(activePage, [
        ...getWorkerInfoDefaultActions(data?.worker),
        {
          id: 'workerSubcontractors',
          groupTitle: 'Companies',
          groupActions: data.worker.subcontractors.map((subcontractor) => ({
            id: subcontractor.id,
            name: subcontractor.company.name,
            href: routes.subcontractors.view(subcontractor.id),
          })),
        },
        ...(hasTickets
          ? [
              {
                id: 'workerTickets',
                groupTitle: 'ticket:plural',
                groupActions: [
                  {
                    id: 'addWorkerTicket',
                    name: t('ticket:buttons.addTicket'),
                    href: routes.workers.newTicket(data.worker.id),
                  },
                  ...data.worker.tickets.map((ticket) => ({
                    id: ticket.id,
                    name: ticket.ticketTemplate.name,
                    icon: IconIdBadge2,
                    description: getWorkerName(data.worker),
                    extra: <BabyTicket ticket={ticket} />,
                    href: routes.workers.view(data.worker.id, {
                      tab: 'tickets',
                    }),
                  })),
                ],
              } as CommandGroup,
            ]
          : []),
        ...(hasInductions
          ? [
              {
                id: 'workerInductions',
                groupTitle: t('induction:plural'),
                groupActions: sortBy(data.worker.inductions, 'createdAt')
                  .reverse()
                  .map((induction) => ({
                    id: induction.id,
                    name: t('induction:titles.workerInduction', {
                      workerName: getWorkerName(data.worker),
                    }),
                    icon: IconCheckbox,
                    description: <TimezonedDate date={induction.createdAt} />,
                    extra: <IconOnlyInductionTag status={induction.status} />,
                    href: routes.workers.view(data.worker.id, {
                      tab: 'inductions',
                    }),
                  })),
              } as CommandGroup,
            ]
          : []),
        ...(hasSwms
          ? [
              {
                id: 'workerSwms',
                groupTitle: t('swms:plural'),
                groupActions: data.worker.documentSignatures.map(
                  (signature) => ({
                    id: signature.id,
                    name: signature.swmsDoc.name,
                    description: (
                      <>
                        Signed <TimezonedDate date={signature.createdAt} />
                      </>
                    ),
                    icon: IconSignature,
                    href: routes.workers.view(data.worker.id, { tab: 'swms' }),
                  })
                ),
              } as CommandGroup,
            ]
          : []),
      ])
    },
  })

  return null
}
